// Distractors and option checks for generated questions.
// The right answer always comes from a FormRef; distractors come from the same paradigm (or nearby roots)
// and are rejected when they are a valid core or विकल्प form for the slot — so no question has two right answers.

import type { FormRef } from '@/prakrit/lib/grammar/formref'
import type { CheckOptions, SentenceSpec } from '@/prakrit/lib/grammar/sentence'
import type {
    Case,
    Num,
    Person,
    Tense
} from '@/prakrit/lib/grammar/types'

import { resolveRef } from '@/prakrit/lib/grammar/formref'
import { checkSentence } from '@/prakrit/lib/grammar/sentence'
import { CASES, NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'

const uniq = (list: string[]) => Array.from(new Set(list))

/** Core forms of the other cells of the same paradigm, nearest cells first. */
export const paradigmNeighbours = (ref: FormRef): FormRef[] => {
    if (ref.kind === 'verb' || ref.kind === 'as') {
        const cells: { person: Person, number: Num }[] = []

        // Same number first (other persons), then the other number.
        for (const p of PERSONS) if (p !== ref.person) cells.push({ person: p, number: ref.number })
        for (const p of PERSONS) cells.push({ person: p, number: ref.number === 'eka' ? 'bahu' : 'eka' })

        if (ref.kind === 'as') return cells.map(c => ({ ...ref, ...c }))

        const sameTense: FormRef[] = cells.map(c => ({ ...ref, ...c }))
        const otherTenses: FormRef[] = (['vartaman', 'bhavishya', 'vidhi', 'bhoot'] as Tense[])
            .filter(t => t !== ref.tense)
            .map(t => ({ ...ref, tense: t }))

        return [...sameTense, ...otherTenses]
    }

    if (ref.kind === 'noun' || ref.kind === 'pronoun') {
        const out: FormRef[] = []
        const cases = (ref.kind === 'pronoun' ? CASES.slice(0, 7) : CASES) as Case[]

        out.push({ ...ref, number: ref.number === 'eka' ? 'bahu' : 'eka' } as FormRef)

        for (const c of cases) {
            for (const n of NUMBERS) {
                if (c === ref.case && n === ref.number) continue

                out.push({ ...ref, case: c, number: n } as FormRef)
            }
        }

        return out
    }

    return []
}

/**
 * Pick distractors for a FormRef answer: never a valid form (core or alt) for the answer slot,
 * never a duplicate, never an unresolvable cell.
 */
export const distractorsFor = (answer: FormRef, count: number, extra: FormRef[] = []): string[] => {
    const target = resolveRef(answer)

    if (!target.ok) return []

    const invalid = new Set(target.valid)
    const out: string[] = []

    for (const ref of [...extra, ...paradigmNeighbours(answer)]) {
        const r = resolveRef(ref)

        if (!r.ok) continue

        const f = r.display

        if (invalid.has(f) || out.includes(f)) continue

        out.push(f)

        if (out.length >= count) break
    }

    return out
}

export interface OptionsCheck {
    ok: boolean
    /** Options that are valid forms for the slot (should be exactly one). */
    validOptions: string[]
    duplicates: string[]
}

/** Is exactly one option a valid form (core or alt) for the answer slot? */
export const checkFormOptions = (answer: FormRef, options: string[]): OptionsCheck => {
    const target = resolveRef(answer)
    const valid = new Set(target.valid)
    const validOptions = options.filter(o => valid.has(o))
    const duplicates = options.filter((o, i) => options.indexOf(o) !== i)

    return { ok: target.ok && validOptions.length === 1 && duplicates.length === 0, validOptions, duplicates }
}

/**
 * For a fill-the-blank sentence: substitute each option and keep only option lists where exactly one
 * makes a grammatical sentence. This catches distractors that are right in another tense (पढीअ fits any person).
 */
export const checkFillOptions = (words: string[], blank: number, options: string[], check?: CheckOptions, spec?: SentenceSpec): OptionsCheck => {
    const grammatical = options.filter(option => {
        const sentence = words.map((w, i) => (i === blank ? option : w))

        return checkSentence(sentence, spec, check).ok
    })
    const duplicates = options.filter((o, i) => options.indexOf(o) !== i)

    return { ok: grammatical.length === 1 && duplicates.length === 0, validOptions: uniq(grammatical), duplicates }
}

/** Deterministic shuffle (mulberry32) so the server can rebuild the same question from its seed. */
export const seededShuffle = <T>(items: T[], seed: number): T[] => {
    const out = [...items]
    let a = seed >>> 0

    const rand = () => {
        a = (a + 0x6D2B79F5) >>> 0

        let t = a

        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }

    for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rand() * (i + 1))

        ;[out[i], out[j]] = [out[j], out[i]]
    }

    return out
}

export const hashString = (value: string): number => {
    let h = 2166136261

    for (let i = 0; i < value.length; i += 1) {
        h ^= value.charCodeAt(i)
        h = Math.imul(h, 16777619)
    }

    return h >>> 0
}
